import type { Rollout, RunConfiguration, TaskInfo } from './runs'
import type { ImplementedRubricSpec } from './types'

export type JobStatus = 'queued' | 'running' | 'completed' | 'failed' | 'terminated'
export type NodeStatus = 'idle' | 'running' | 'requested' | 'unreachable' | 'terminated'
export interface PoolSummary {
  id: number; name: string; program: string; framework: string; retired_at?: string | null
  jobs: Record<JobStatus, number>; nodes: Record<Exclude<NodeStatus, 'terminated'>, number>
  machines?: {id: number; instance_id: string | null; slots: number; occupied: number; available: number; state: string}[]
  capacity?: { nodes: number; slots: number; occupied: number; available: number; unavailable: number; pending_slots: number; isolated_slots: number; isolated_occupied: number }
}
export interface TaskRunSummary {
  task_id: string; title: string; program: string; stage: number | null; display_stage: number | null; lifecycle: string
  current_package_key: string | null; total: number; completed: number; failed: number
  queued: number; running: number; terminated: number; scored: number; mean_score: number | null
  models: string[]; last_activity: string | null
  other_running?: number; other_queued?: number
  workflow_id?: string; workflow_title?: string
  entry_type?: 'workflow'; task_count?: number
}
export interface HandSet { set_by: { user_id: string; name: string | null }; set_at: string; note: string }
/** The task's hand-set latest package; `active` is false once a newer agent package superseded it. */
export interface HandLatest extends HandSet { commit: string; active: boolean }
export interface PackageVersion {
  latest?: boolean
  status?: { kind: 'submission' | 'hand' | 'wip' | 'unknown'; hand?: HandSet | null; review_unavailable?: boolean; unreviewed: boolean; published: boolean; rejected: boolean
    pull_request?: { number: number; url: string } | null; submitted_at?: string | null }
  package_key: string | null; current: boolean; latest_run?: boolean; identity_incomplete: boolean
  reference: { repository?: string; commit?: string; program?: string; task_id?: string } | null
  published_at: string | null; observed_at?: string | null; model_jobs: number; checks: number
}
export interface BoardJob {
  id: number; experiment_id: string; job_id: string; task_id: string; model: string | null
  task_title?: string
  package_commit?: string | null; history_complete?: boolean | null
  kind: string; status: JobStatus; package_key: string | null; attempt_id: string | null
  score: number | null; step: number | null; created_at: string; finished_at: string | null
  preview: { url: string; key?: string; kind: 'final' | 'last_step'; sequence: number | null; width?: number; height?: number } | null
}
export interface PoolNode {
  machine_id: number; instance_id: string | null; instance_type: string; category: NodeStatus
  state: string; slots: number; busy: number; free: number; draining: boolean
  last_seen: string | null; age_seconds: number | null; reason: string | null
  jobs: { experiment_id: string; job_id: string; attempt_id: string; task_id: string; model: string }[]
}
export interface JobInspection {
  rollout: Rollout; configuration: RunConfiguration; task: TaskInfo; rubric_map: ImplementedRubricSpec | null
}
export interface Page<T> { next_offset: number | null; jobs?: T[]; tasks?: T[]; nodes?: T[] }

export function queryPath(path: string, values: Record<string, string | number | null | undefined>) {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(values)) if (value != null && value !== '') query.set(key, String(value))
  return path + (query.size ? `?${query}` : '')
}
